// istanbul ignore file

import React from 'react';
import parse, { domToReact } from 'html-react-parser';
import { HashLink } from 'react-router-hash-link';
import { Link } from 'react-router-dom';

const INTERNAL_HOSTS = [
  'localhost',
  'dev-client.nonprod.cheznicu.io',
  'stage-client.nonprod.cheznicu.io',
  'cheznicu.io',
];

// NOTE: Verify this path matches your actual folder structure!
// If your GlossaryItem is located somewhere else, update this import path.
import GlossaryItem from '@/components/GlossaryItem/GlossaryItem'; 

const isNested = (node, depth = 0) => {
  if (node && depth < 4) {
    const { parent } = node;
    return (
      (parent && parent.attribs['data-glossary-id']) ||
      isNested(parent, depth + 1)
    );
  }
  return false;
};

// TODO: All of this functionality should be moved to the server within the short code function on WordPress
export const stripPTags = content => {
  // SAFEGUARD: Ensure content is actually a string before running Regex
  if (!content || typeof content !== 'string') {
    return ''; 
  }

  let contentReplacement = content;
  contentReplacement = contentReplacement.replace(new RegExp('p>', 'g'), 'div>');
  contentReplacement = contentReplacement.replace(
    new RegExp('p class="CHNbody">', 'g'),
    'div>'
  ); // Account for some of the content the client is copying and pasting.
  contentReplacement = contentReplacement.replace(
    new RegExp('div>', 'g'),
    'div class="content-formatted">'
  );
  contentReplacement = contentReplacement.replace(
    new RegExp('>="glossary-item">', 'g'),
    '>'
  );
  contentReplacement = contentReplacement.replace(new RegExp('<<', 'g'), '<');

  return contentReplacement;
};

/**
 * Format Content for React (html-react-parser version)
 *
 * Converts WordPress HTML content to React, replacing anchors and glossary items.
 */
export const formatContentForReact = content => {
  // SAFEGUARD: Don't bother parsing empty/invalid content
  if (!content || typeof content !== 'string') return null;

  const htmlInput = stripPTags(content);
  const headerHeightOffset = typeof window !== 'undefined' && window.innerWidth < 1024 ? 0 : -136;

  return parse(htmlInput, {
    replace: (node) => {
      if (node.type === 'tag' && node.name === 'a' && node.attribs && node.attribs['data-reference-id']) {
        // Reference anchor → HashLink
        return (
          <HashLink
            to={node.attribs.href}
            className="reference-link"
            data-reference-id={node.attribs['data-reference-id']}
            scroll={el => {
              el.scrollIntoView({ behavior: 'instant', block: 'start' });
              window.scrollBy(0, headerHeightOffset);
            }}
          >
            {domToReact(node.children)}
          </HashLink>
        );
      }
      if (node.type === 'tag' && node.name === 'a' && node.attribs?.href) {
        let href = node.attribs.href;
        try {
          const url = new URL(href);
          if (INTERNAL_HOSTS.some(h => url.hostname === h || url.hostname.endsWith(`.${h}`))) {
            href = url.pathname + url.search + url.hash;
          }
        } catch {
        }
        if (!href.startsWith('http')) {
          const { href: _h, ...rest } = node.attribs;
          return (
            <Link to={href} {...rest}>
              {domToReact(node.children)}
            </Link>
          );
        }
      }
      if (node.type === 'tag' && node.attribs && node.attribs['data-glossary-id']) {
        // Glossary item
        if (isNested(node)) {
          // Nested: link to glossary
          const children = domToReact(node.children);
          const title = (children[1]?.props?.children || '').toLowerCase().replace(' ', '-');
          return (
            <a href={`/glossary#${title}`}>{children[0]}</a>
          );
        }
        // Not nested: GlossaryItem component
        const children = domToReact(node.children);
        return (
          children[0] && children[1] && children[2] && (
            <GlossaryItem
              anchor={children[0]}
              title={children[1]}
              content={children[2]}
            />
          )
        );
      }
      return undefined;
    }
  });
};
